import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  CreateOpportunityInput,
  CreateOpportunityNoteInput,
  CreateOpportunityProductInput,
  CpqQuoteBackfillResult,
  CpqQuoteSyncInput,
  CpqQuoteSyncResult,
  CpqQuoteTemplate,
  CreateCpqQuoteFromTemplateResult,
  Opportunity,
  OpportunityAttachment,
  OpportunityAttachmentUrl,
  OpportunityCpqQuoteLink,
  OpportunityForecast,
  OpportunityInlineImage,
  OpportunityNote,
  OpportunityProduct,
  OpportunityStage,
  OpportunityStatus,
  PaginatedResult,
  ResolveInlineImagesResult,
  SortDirection,
  UpdateOpportunityInput,
  UpdateOpportunityNoteInput,
  UpdateOpportunityProductInput,
} from '@catalyst-psa/shared';

import { api } from '../../api/client.js';
import { buildQuery } from '../../api/queryString.js';

// ---------------------------------------------------------------------------
// Opportunities
// ---------------------------------------------------------------------------

export interface OpportunityListParams {
  page: number;
  pageSize: number;
  customerId?: string;
  salesRepId?: string;
  salesRepIds?: string[];
  serviceCategoryId?: string;
  stage?: OpportunityStage;
  status?: OpportunityStatus;
  /** Inclusive close-date range bounds (yyyy-mm-dd). */
  closeDateFrom?: string;
  closeDateTo?: string;
  sortBy?: string;
  sortDir?: SortDirection;
}

const opportunityKeys = {
  all: ['opportunities'] as const,
  list: (params: OpportunityListParams) => ['opportunities', 'list', params] as const,
  forecast: ['opportunities', 'forecast'] as const,
  detail: (id: string) => ['opportunities', 'detail', id] as const,
  products: (opportunityId: string) => ['opportunities', opportunityId, 'products'] as const,
  notes: (opportunityId: string) => ['opportunities', opportunityId, 'notes'] as const,
  attachments: (opportunityId: string) => ['opportunities', opportunityId, 'attachments'] as const,
};

export function useOpportunities(
  params: OpportunityListParams,
  enabled = true,
): UseQueryResult<PaginatedResult<Opportunity>> {
  return useQuery({
    queryKey: opportunityKeys.list(params),
    enabled,
    queryFn: () =>
      api.get<PaginatedResult<Opportunity>>(
        `/opportunities${buildQuery({
          page: params.page,
          pageSize: params.pageSize,
          customerId: params.customerId,
          salesRepId: params.salesRepId,
          salesRepIds: params.salesRepIds?.join(','),
          serviceCategoryId: params.serviceCategoryId,
          stage: params.stage,
          status: params.status,
          closeDateFrom: params.closeDateFrom,
          closeDateTo: params.closeDateTo,
          sortBy: params.sortBy,
          sortDir: params.sortDir,
        })}`,
      ),
  });
}

export function useOpportunity(id: string | null): UseQueryResult<Opportunity> {
  return useQuery({
    queryKey: opportunityKeys.detail(id ?? 'none'),
    enabled: Boolean(id),
    queryFn: () => api.get<Opportunity>(`/opportunities/${id ?? ''}`),
  });
}

/** Filter subset that the forecast panel shares with the opportunities list. */
export interface OpportunityForecastParams {
  customerId?: string;
  salesRepId?: string;
  salesRepIds?: string[];
  serviceCategoryId?: string;
  stage?: OpportunityStage;
  status?: OpportunityStatus;
}

export function useOpportunityForecast(
  params: OpportunityForecastParams = {},
  enabled = true,
): UseQueryResult<OpportunityForecast> {
  return useQuery({
    queryKey: [...opportunityKeys.forecast, params] as const,
    queryFn: () =>
      api.get<OpportunityForecast>(
        `/opportunities/forecast${buildQuery({
          customerId: params.customerId,
          salesRepId: params.salesRepId,
          salesRepIds: params.salesRepIds?.join(','),
          serviceCategoryId: params.serviceCategoryId,
          stage: params.stage,
          status: params.status,
        })}`,
      ),
    staleTime: 60_000,
    enabled,
  });
}

export function useCreateOpportunity(): UseMutationResult<
  Opportunity,
  unknown,
  CreateOpportunityInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateOpportunityInput) => api.post<Opportunity>('/opportunities', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: opportunityKeys.all });
    },
  });
}

export interface UpdateOpportunityArgs {
  id: string;
  input: UpdateOpportunityInput;
}

export function useUpdateOpportunity(): UseMutationResult<
  Opportunity,
  unknown,
  UpdateOpportunityArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: UpdateOpportunityArgs) =>
      api.patch<Opportunity>(`/opportunities/${id}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: opportunityKeys.all });
    },
  });
}

export function useDeleteOpportunity(): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/opportunities/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: opportunityKeys.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Opportunity ↔ ConnectWise CPQ quote link
// ---------------------------------------------------------------------------

const cpqQuoteKey = (id: string): readonly string[] => ['opportunities', id, 'cpq-quote'];

export function useOpportunityCpqQuote(id: string | null): UseQueryResult<OpportunityCpqQuoteLink> {
  return useQuery({
    queryKey: cpqQuoteKey(id ?? 'none'),
    enabled: Boolean(id),
    queryFn: () => api.get<OpportunityCpqQuoteLink>(`/opportunities/${id ?? ''}/cpq-quote`),
    staleTime: 30_000,
  });
}

export function useLinkOpportunityCpqQuote(
  id: string,
): UseMutationResult<OpportunityCpqQuoteLink, unknown, number> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (quoteNumber: number) =>
      api.post<OpportunityCpqQuoteLink>(`/opportunities/${id}/cpq-quote/link`, { quoteNumber }),
    onSuccess: (data) => {
      queryClient.setQueryData(cpqQuoteKey(id), data);
      void queryClient.invalidateQueries({ queryKey: opportunityKeys.all });
    },
  });
}

export function useUnlinkOpportunityCpqQuote(
  id: string,
): UseMutationResult<OpportunityCpqQuoteLink, unknown, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<OpportunityCpqQuoteLink>(`/opportunities/${id}/cpq-quote`),
    onSuccess: (data) => {
      queryClient.setQueryData(cpqQuoteKey(id), data);
      void queryClient.invalidateQueries({ queryKey: opportunityKeys.all });
    },
  });
}

export function useSyncOpportunityCpqQuote(
  id: string,
): UseMutationResult<CpqQuoteSyncResult, unknown, CpqQuoteSyncInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CpqQuoteSyncInput) =>
      api.post<CpqQuoteSyncResult>(`/opportunities/${id}/cpq-quote/sync`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: opportunityKeys.products(id) });
      void queryClient.invalidateQueries({ queryKey: opportunityKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: cpqQuoteKey(id) });
      void queryClient.invalidateQueries({ queryKey: opportunityKeys.all });
    },
  });
}

export function useBackfillOpportunityCpqQuotes(): UseMutationResult<
  CpqQuoteBackfillResult,
  unknown,
  void
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<CpqQuoteBackfillResult>('/opportunities/cpq-quote/backfill'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: opportunityKeys.all });
    },
  });
}

export function useCpqQuoteTemplates(enabled: boolean): UseQueryResult<CpqQuoteTemplate[]> {
  return useQuery({
    queryKey: ['opportunities', 'cpq-quote', 'templates'],
    enabled,
    queryFn: () => api.get<CpqQuoteTemplate[]>('/opportunities/cpq-quote/templates'),
    staleTime: 5 * 60_000,
  });
}

export function useCreateOpportunityCpqQuoteFromTemplate(
  id: string,
): UseMutationResult<CreateCpqQuoteFromTemplateResult, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (templateId: string) =>
      api.post<CreateCpqQuoteFromTemplateResult>(
        `/opportunities/${id}/cpq-quote/create-from-template`,
        { templateId },
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(cpqQuoteKey(id), data.link);
      void queryClient.invalidateQueries({ queryKey: cpqQuoteKey(id) });
      void queryClient.invalidateQueries({ queryKey: opportunityKeys.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Opportunity products
// ---------------------------------------------------------------------------

export function useOpportunityProducts(
  opportunityId: string,
): UseQueryResult<OpportunityProduct[]> {
  return useQuery({
    queryKey: opportunityKeys.products(opportunityId),
    enabled: Boolean(opportunityId),
    queryFn: () => api.get<OpportunityProduct[]>(`/opportunities/${opportunityId}/products`),
  });
}

export interface CreateOpportunityProductArgs {
  opportunityId: string;
  input: CreateOpportunityProductInput;
}

export function useCreateOpportunityProduct(): UseMutationResult<
  OpportunityProduct,
  unknown,
  CreateOpportunityProductArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ opportunityId, input }: CreateOpportunityProductArgs) =>
      api.post<OpportunityProduct>(`/opportunities/${opportunityId}/products`, input),
    onSuccess: (_data, { opportunityId }) => {
      void queryClient.invalidateQueries({ queryKey: opportunityKeys.products(opportunityId) });
    },
  });
}

export interface UpdateOpportunityProductArgs {
  opportunityId: string;
  productLineId: string;
  input: UpdateOpportunityProductInput;
}

export function useUpdateOpportunityProduct(): UseMutationResult<
  OpportunityProduct,
  unknown,
  UpdateOpportunityProductArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ opportunityId, productLineId, input }: UpdateOpportunityProductArgs) =>
      api.patch<OpportunityProduct>(
        `/opportunities/${opportunityId}/products/${productLineId}`,
        input,
      ),
    onSuccess: (_data, { opportunityId }) => {
      void queryClient.invalidateQueries({ queryKey: opportunityKeys.products(opportunityId) });
    },
  });
}

export interface DeleteOpportunityProductArgs {
  opportunityId: string;
  productLineId: string;
}

export function useDeleteOpportunityProduct(): UseMutationResult<
  void,
  unknown,
  DeleteOpportunityProductArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ opportunityId, productLineId }: DeleteOpportunityProductArgs) =>
      api.delete(`/opportunities/${opportunityId}/products/${productLineId}`),
    onSuccess: (_data, { opportunityId }) => {
      void queryClient.invalidateQueries({ queryKey: opportunityKeys.products(opportunityId) });
    },
  });
}

// ---------------------------------------------------------------------------
// Opportunity notes (append-only running log)
// ---------------------------------------------------------------------------

export function useOpportunityNotes(opportunityId: string): UseQueryResult<OpportunityNote[]> {
  return useQuery({
    queryKey: opportunityKeys.notes(opportunityId),
    enabled: Boolean(opportunityId),
    queryFn: () => api.get<OpportunityNote[]>(`/opportunities/${opportunityId}/notes`),
  });
}

export interface CreateOpportunityNoteArgs {
  opportunityId: string;
  input: CreateOpportunityNoteInput;
}

export function useCreateOpportunityNote(): UseMutationResult<
  OpportunityNote,
  unknown,
  CreateOpportunityNoteArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ opportunityId, input }: CreateOpportunityNoteArgs) =>
      api.post<OpportunityNote>(`/opportunities/${opportunityId}/notes`, input),
    onSuccess: (_data, { opportunityId }) => {
      void queryClient.invalidateQueries({ queryKey: opportunityKeys.notes(opportunityId) });
    },
  });
}

export interface UpdateOpportunityNoteArgs {
  opportunityId: string;
  noteId: string;
  input: UpdateOpportunityNoteInput;
}

export function useUpdateOpportunityNote(): UseMutationResult<
  OpportunityNote,
  unknown,
  UpdateOpportunityNoteArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ opportunityId, noteId, input }: UpdateOpportunityNoteArgs) =>
      api.patch<OpportunityNote>(`/opportunities/${opportunityId}/notes/${noteId}`, input),
    onSuccess: (_data, { opportunityId }) => {
      void queryClient.invalidateQueries({ queryKey: opportunityKeys.notes(opportunityId) });
    },
  });
}

export interface DeleteOpportunityNoteArgs {
  opportunityId: string;
  noteId: string;
}

export function useDeleteOpportunityNote(): UseMutationResult<
  void,
  unknown,
  DeleteOpportunityNoteArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ opportunityId, noteId }: DeleteOpportunityNoteArgs) =>
      api.delete(`/opportunities/${opportunityId}/notes/${noteId}`),
    onSuccess: (_data, { opportunityId }) => {
      void queryClient.invalidateQueries({ queryKey: opportunityKeys.notes(opportunityId) });
    },
  });
}

// ---------------------------------------------------------------------------
// Attachments (SharePoint-backed) & inline note images
// ---------------------------------------------------------------------------

export function useOpportunityAttachments(
  opportunityId: string,
): UseQueryResult<OpportunityAttachment[]> {
  return useQuery({
    queryKey: opportunityKeys.attachments(opportunityId),
    queryFn: () => api.get<OpportunityAttachment[]>(`/opportunities/${opportunityId}/attachments`),
  });
}

export interface UploadAttachmentArgs {
  opportunityId: string;
  file: File;
}

export function useUploadOpportunityAttachment(): UseMutationResult<
  OpportunityAttachment,
  unknown,
  UploadAttachmentArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ opportunityId, file }: UploadAttachmentArgs) => {
      const form = new FormData();
      form.append('file', file);
      return api.post<OpportunityAttachment>(`/opportunities/${opportunityId}/attachments`, form);
    },
    onSuccess: (_data, { opportunityId }) => {
      void queryClient.invalidateQueries({ queryKey: opportunityKeys.attachments(opportunityId) });
    },
  });
}

export interface DeleteAttachmentArgs {
  opportunityId: string;
  attachmentId: string;
}

export function useDeleteOpportunityAttachment(): UseMutationResult<
  void,
  unknown,
  DeleteAttachmentArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ opportunityId, attachmentId }: DeleteAttachmentArgs) =>
      api.delete(`/opportunities/${opportunityId}/attachments/${attachmentId}`),
    onSuccess: (_data, { opportunityId }) => {
      void queryClient.invalidateQueries({ queryKey: opportunityKeys.attachments(opportunityId) });
    },
  });
}

/** Fetches a fresh embeddable preview URL for an attachment (no download). */
export function fetchAttachmentPreviewUrl(
  opportunityId: string,
  attachmentId: string,
): Promise<OpportunityAttachmentUrl> {
  return api.get<OpportunityAttachmentUrl>(
    `/opportunities/${opportunityId}/attachments/${attachmentId}/preview`,
  );
}

/** Downloads an attachment's bytes through the API and triggers a browser save. */
export async function downloadOpportunityAttachment(
  opportunityId: string,
  attachmentId: string,
): Promise<void> {
  const { blob, fileName } = await api.download(
    `/opportunities/${opportunityId}/attachments/${attachmentId}/download`,
  );
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName ?? 'download';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Uploads a pasted image to SharePoint and returns its id + a fresh display URL. */
export function uploadOpportunityImage(
  opportunityId: string,
  file: File,
): Promise<OpportunityInlineImage> {
  const form = new FormData();
  form.append('file', file);
  return api.post<OpportunityInlineImage>(
    `/opportunities/${opportunityId}/attachments/images`,
    form,
  );
}

/** Resolves inline-image attachment ids to fresh display URLs for rendering. */
export function resolveOpportunityImages(
  opportunityId: string,
  ids: string[],
): Promise<ResolveInlineImagesResult> {
  return api.post<ResolveInlineImagesResult>(
    `/opportunities/${opportunityId}/attachments/images/resolve`,
    { ids },
  );
}
